// HealthKpiBar — homepage strip showing the cross-page health snapshot.
// Pulls /api/dashboard/health (one cheap call) and renders 5 hero cards so
// any employee can read the company's pulse in 3 seconds.
//
// RBAC (Feb 2026): absolute monetary values (sales, payroll) are masked for
// non-financial roles — they see a percentage / headcount only, never raw
// SAR figures. Admin, CEO, operations, finance, and employees_manager
// (for payroll) keep the full numbers.
import React, { useEffect, useState } from "react";
import { api } from "./lib/api";
import { useAuth } from "./lib/auth";

const fmtN = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
const fmtPct = (n) => n == null ? "—" : `${Number(n).toFixed(1)}%`;

export default function HealthKpiBar() {
  const { user } = useAuth();
  const role = (user?.role || "").toLowerCase();
  const canSeeMoney = ["admin", "ceo", "operations", "finance"].includes(role);
  const canSeePayroll = ["admin", "ceo", "finance", "employees_manager"].includes(role);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const { data: d } = await api.get("/dashboard/health"); setData(d); }
      catch (_e) { /* silent — homepage degrades gracefully */ }
      setLoading(false);
    })();
  }, []);

  const fin = data?.finance;
  const ms = data?.mystery;
  const cust = data?.customers;
  const sales = data?.sales;
  const emp = data?.employees;
  const runway = data?.cash_runway;
  const att = data?.attendance;
  const comp = data?.complaints;

  const cards = [];

  // 1) Break-even progress (most important number for a startup cafe)
  if (fin) {
    const missing = fin.break_even_progress_pct == null;  // F1 — لم تُدخل نقطة التعادل لأي فرع
    const p = fin.break_even_progress_pct ?? 0;
    const tone = missing || fin.no_sales_yet ? "warn" : p >= 100 ? "success" : p >= 80 ? "warn" : "danger";
    const sub = missing ? "لم تُدخل نقطة التعادل (الإعدادات ← نقطة التعادل اليومية)" : fin.is_profitable
      ? "متجاوز نقطة التعادل ✓"
      : (fin.required_daily_revenue != null
          ? `تحتاج ${fmtN(fin.required_daily_revenue)} ر.س/يوم لبقية الشهر (${fin.remaining_days} يوم) لتحقيق التعادل`
          : fin.no_sales_yet
            ? "لا مبيعات مسجّلة بعد لهذا الشهر"
            : "—");
    cards.push({
      icon: "🎯",
      label: `تحقيق نقطة التعادل${fin.break_even_level ? ` (${fin.break_even_level})` : ""}`,
      value: missing ? "—" : `${fmtPct(p)}`,
      sub,
      tone,
      progress: Math.min(p, 100),
      testid: "hk-be",
      period: fin.period,
    });
  }

  // 2) Customer retention (repeat / unique) — ALL-TIME cumulative KPI
  if (cust && cust.retention_pct != null) {
    const r = cust.retention_pct;
    const tone = r >= 50 ? "success" : r >= 30 ? "warn" : "danger";
    cards.push({
      icon: "🔁",
      label: "الاحتفاظ بالعملاء (تراكمي)",
      value: fmtPct(r),
      sub: `${fmtN(cust.repeat_customers)} من ${fmtN(cust.unique_customers)} عميل عادوا مرة على الأقل`,
      tone,
      progress: r,
      testid: "hk-retention",
    });
  }

  // 3) Mystery shopper trend
  if (ms) {
    const s = ms.avg_30d ?? ms.latest_score;
    const tone = s >= 85 ? "success" : s >= 70 ? "warn" : "danger";
    cards.push({
      icon: "🕵️",
      label: "جودة الخدمة",
      value: fmtPct(s),
      sub: `${fmtN(ms.evaluations_count)} تقييم · متوسط آخر 30`,
      tone,
      progress: s,
      testid: "hk-quality",
    });
  }

  // v119.123: Cash Runway (months of runway remaining)
  if (runway && runway.months != null && canSeeMoney) {
    const m = runway.months;
    const tone = m >= 6 ? "success" : m >= 3 ? "warn" : "danger";
    cards.push({
      icon: "💵",
      label: "احتياطي النقد (أشهر)",
      value: `${m} شهر`,
      sub: `نقد: ${fmtN(runway.cash)} ر.س · إنفاق شهري: ${fmtN(runway.burn)} ر.س`,
      tone,
      progress: Math.min((m / 12) * 100, 100),
      testid: "hk-runway",
    });
  }

  // v119.123: Attendance rate (last 30 days)
  if (att && att.rate_pct != null) {
    const r = att.rate_pct;
    const tone = r >= 95 ? "success" : r >= 85 ? "warn" : "danger";
    cards.push({
      icon: "🕒",
      label: "معدل الحضور (30 يوم)",
      value: fmtPct(r),
      sub: `في الوقت: ${fmtPct(att.on_time_pct)} · ${fmtN(att.records)} سجل`,
      tone,
      progress: r,
      testid: "hk-attendance",
    });
  }

  // v119.123: Complaints resolution rate (last 30 days)
  if (comp) {
    const r = comp.resolved_pct ?? 0;
    const tone = r >= 80 ? "success" : r >= 60 ? "warn" : "danger";
    cards.push({
      icon: "📢",
      label: "معالجة الشكاوى (30 يوم)",
      value: `${r}%`,
      sub: `${fmtN(comp.resolved)} مغلقة · ${fmtN(comp.open)} مفتوحة`,
      tone,
      progress: r,
      testid: "hk-complaints",
    });
  }

  // 4) Sales — masked as percentage growth for non-financial roles.
  if (sales) {
    if (canSeeMoney) {
      cards.push({
        icon: "💰",
        label: "المبيعات (آخر 30 يوم)",
        value: `${fmtN(sales.total_30d)} ر.س`,
        sub: `متوسط يومي ${fmtN(sales.avg_daily)} · مصدر: تقرير POS`,
        tone: "brand",
        testid: "hk-sales",
      });
    } else if (sales.growth_pct_vs_prev_30d != null) {
      // Show only growth %, hide absolute SAR
      const g = sales.growth_pct_vs_prev_30d;
      const tone = g >= 5 ? "success" : g >= -5 ? "warn" : "danger";
      cards.push({
        icon: "📈",
        label: "نمو المبيعات (شهر/شهر)",
        value: `${g >= 0 ? "+" : ""}${fmtPct(g)}`,
        sub: g >= 0 ? "أداء صاعد مقارنة بالشهر الماضي" : "تراجع — يحتاج تحريك حملات",
        tone,
        progress: Math.min(Math.max(50 + g * 2, 0), 100),
        testid: "hk-sales-growth",
      });
    }
  }

  // 5) Headcount + total payroll (payroll masked for non-finance)
  if (emp) {
    cards.push({
      icon: "👥",
      label: "الموظفون",
      value: fmtN(emp.headcount),
      sub: canSeePayroll
        ? `إجمالي رواتب ${fmtN(emp.total_salary_gross || emp.total_salary_basic || 0)} ر.س`
        : "إجمالي عدد الفريق",
      tone: "brand",
      testid: "hk-employees",
    });
  }

  // Permanent visibility: even during loading or when no data, show the bar skeleton
  // so the homepage health strip never disappears unexpectedly.
  if (!cards.length) {
    return (
      <div data-testid="health-kpi-bar" style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: "var(--brown-deep)", marginBottom: 8, paddingInlineStart: 8, borderInlineStart: "3px solid var(--gold-deep)" }}>
          📍 صحة الشركة اليوم
        </div>
        <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(200px, 1fr))`, gap: 10 }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} data-testid={`hk-skeleton-${i}`} style={{
              padding: 12, background: "rgba(140,95,50,0.04)", border: "1px solid var(--border)",
              borderRadius: 12, minHeight: 92, opacity: 0.6,
            }}>
              <div style={{ height: 12, width: "60%", background: "rgba(0,0,0,0.06)", borderRadius: 4, marginBottom: 10 }} />
              <div style={{ height: 22, width: "40%", background: "rgba(0,0,0,0.08)", borderRadius: 4, marginBottom: 8 }} />
              <div style={{ height: 10, width: "80%", background: "rgba(0,0,0,0.05)", borderRadius: 4 }} />
            </div>
          ))}
        </div>
        {!loading && !data && (
          <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 6, paddingInlineStart: 8 }}>
            تعذّر تحميل المؤشرات الآن — سيتم التحديث تلقائياً عند توفر البيانات.
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="health-kpi-bar" style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: "var(--brown-deep)", marginBottom: 8, paddingInlineStart: 8, borderInlineStart: "3px solid var(--gold-deep)" }}>
        📍 صحة الشركة اليوم
      </div>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(200px, 1fr))`, gap: 10 }}>
        {cards.map((c) => <HealthCard key={c.testid} {...c} />)}
      </div>
    </div>
  );
}

function HealthCard({ icon, label, value, sub, tone = "brand", progress, testid, period }) {
  const palette = {
    success: { bg: "rgba(34,197,94,0.07)", border: "rgba(34,197,94,0.3)", v: "var(--success, #2e8b57)", bar: "#22c55e" },
    warn: { bg: "rgba(245,158,11,0.07)", border: "rgba(245,158,11,0.3)", v: "var(--warn, #c9a44a)", bar: "#f59e0b" },
    danger: { bg: "rgba(185,83,62,0.08)", border: "rgba(185,83,62,0.3)", v: "var(--danger, #b9533e)", bar: "#dc2626" },
    brand: { bg: "rgba(140,95,50,0.06)", border: "var(--border)", v: "var(--brown-deep)", bar: "#8b6f47" },
  }[tone] || {};

  return (
    <div data-testid={testid} style={{
      padding: 12, background: palette.bg, border: `1px solid ${palette.border}`,
      borderRadius: 12, position: "relative", overflow: "hidden",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
        <span style={{ fontSize: 18 }}>{icon}</span>
        <div style={{ fontSize: 11.5, color: "var(--muted)", fontWeight: 600 }}>{label}</div>
      </div>
      <div className="hkpi-value" style={{ fontSize: 24, fontWeight: 900, color: palette.v, lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4, lineHeight: 1.5 }}>{sub}</div>}
      {period && <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 2, fontStyle: "italic" }}>الفترة: {period}</div>}
      {progress != null && (
        <div style={{ marginTop: 8, height: 5, background: "rgba(0,0,0,0.06)", borderRadius: 99, overflow: "hidden" }}>
          <div style={{ width: `${Math.min(progress, 100)}%`, height: "100%", background: palette.bar, transition: "width 0.4s ease" }} />
        </div>
      )}
    </div>
  );
}
